/**
 * Brown Butters — product gallery.
 *
 * A vertical thumbnail rail drives a horizontally snapping stage. Everything degrades:
 * with JS off the stage is still a scroll-snap carousel (swipeable on touch), and the
 * thumbnails simply stop being clickable.
 *
 * Responsibilities:
 *   1. Thumb / arrow / keyboard selection, kept in sync with manual swipes.
 *   2. A <dialog> lightbox for zoom, with its own keyboard navigation.
 *   3. Following the buy box: `shopify:product:select` carries a media id, so choosing a
 *      fragrance moves the gallery to that variant's image.
 *
 * Dependency-free on purpose — it only talks to the DOM and to Shopify's standard event.
 */

const PRODUCT_SELECT_EVENT = 'shopify:product:select';

class MbbProductGallery extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #syncingScroll = false;

  /** @type {number} */
  #scrollFrame = 0;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.stage = this.querySelector('[data-mbb-track]');
    this.slides = Array.from(this.querySelectorAll('[data-mbb-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-mbb-thumb]'));
    this.rail = this.querySelector('[data-mbb-thumbs]');
    this.lightbox = this.querySelector('[data-mbb-lightbox]');
    this.lightboxImage = this.querySelector('[data-mbb-lightbox-image]');
    this.lightboxCounter = this.querySelector('[data-mbb-lightbox-counter]');

    if (!this.slides.length) return;

    // Only now that JS is running do the thumbnails become interactive.
    this.dataset.enhanced = 'true';

    for (const [index, thumb] of this.thumbs.entries()) {
      thumb.addEventListener('click', () => this.select(index), { signal });
    }

    this.rail?.addEventListener('keydown', this.#handleRailKeys, { signal });

    for (const button of this.querySelectorAll('[data-mbb-step]')) {
      button.addEventListener(
        'click',
        () => this.select(this.#index + Number(button.getAttribute('data-mbb-step') || 0)),
        { signal }
      );
    }

    this.stage?.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

    for (const opener of this.querySelectorAll('[data-mbb-zoom]')) {
      opener.addEventListener('click', this.#openLightbox, { signal });
    }

    if (this.lightbox) {
      this.lightbox.addEventListener('keydown', this.#handleLightboxKeys, { signal });
      this.lightbox.addEventListener('click', this.#handleLightboxClick, { signal });
      for (const button of this.lightbox.querySelectorAll('[data-mbb-lightbox-step]')) {
        button.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.select(this.#index + Number(button.getAttribute('data-mbb-lightbox-step') || 0));
            this.#paintLightbox();
          },
          { signal }
        );
      }
      this.lightbox
        .querySelector('[data-mbb-lightbox-close]')
        ?.addEventListener('click', () => this.lightbox?.close(), { signal });
    }

    document.addEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect, { signal });

    this.#paint();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
  }

  get count() {
    return this.slides.length;
  }

  /** Whether the section was configured to wrap around at either end. */
  get loops() {
    return this.dataset.loop === 'true';
  }

  /**
   * Moves the gallery to `index`, clamping or wrapping as configured.
   * @param {number} index
   */
  select(index) {
    if (!this.count) return;

    let next = index;
    if (this.loops) {
      next = ((index % this.count) + this.count) % this.count;
    } else {
      next = Math.min(Math.max(index, 0), this.count - 1);
    }

    if (next === this.#index) return;
    this.#index = next;
    this.#paint();
    this.#scrollToActive();
    this.#paintLightbox();
  }

  #paint() {
    for (const [index, thumb] of this.thumbs.entries()) {
      const active = index === this.#index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    }

    for (const [index, slide] of this.slides.entries()) {
      slide.classList.toggle('is-active', index === this.#index);
    }

    for (const button of this.querySelectorAll('[data-mbb-step]')) {
      if (!(button instanceof HTMLButtonElement) || this.loops) continue;
      const step = Number(button.getAttribute('data-mbb-step') || 0);
      button.disabled = step < 0 ? this.#index === 0 : this.#index === this.count - 1;
    }
  }

  #scrollToActive() {
    const slide = this.slides[this.#index];
    if (!slide || !this.stage) return;

    // Suppress the scroll handler so the programmatic move doesn't fight the observer.
    this.#syncingScroll = true;
    this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft, behavior: 'smooth' });
    window.setTimeout(() => {
      this.#syncingScroll = false;
    }, 400);

    this.thumbs[this.#index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  #handleScroll = () => {
    if (this.#syncingScroll || this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      if (!this.stage) return;

      const center = this.stage.scrollLeft + this.stage.clientWidth / 2;
      let closest = 0;
      let smallest = Infinity;

      for (const [index, slide] of this.slides.entries()) {
        const slideCenter = slide.offsetLeft - this.stage.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < smallest) {
          smallest = distance;
          closest = index;
        }
      }

      if (closest === this.#index) return;
      this.#index = closest;
      this.#paint();
      this.thumbs[closest]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  /** @param {KeyboardEvent} event */
  #handleRailKeys = (event) => {
    const steps = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 };
    let next = null;

    if (event.key in steps) next = this.#index + steps[/** @type {keyof steps} */ (event.key)];
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.count - 1;
    if (next === null) return;

    event.preventDefault();
    this.select(next);
    this.thumbs[this.#index]?.focus();
  };

  #openLightbox = () => {
    if (!(this.lightbox instanceof HTMLDialogElement)) return;
    this.#paintLightbox();
    this.lightbox.showModal();
  };

  #paintLightbox() {
    if (!this.lightbox) return;

    const slide = this.slides[this.#index];
    const source = slide?.getAttribute('data-mbb-full');
    if (this.lightboxImage instanceof HTMLImageElement && source) {
      this.lightboxImage.removeAttribute('srcset');
      this.lightboxImage.src = source;
      this.lightboxImage.alt = slide?.getAttribute('data-mbb-alt') || '';
    }

    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.#index + 1} / ${this.count}`;
    }
  }

  /** @param {KeyboardEvent} event */
  #handleLightboxKeys = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.select(this.#index + (event.key === 'ArrowLeft' ? -1 : 1));
    this.#paintLightbox();
  };

  /** Clicking the backdrop (the dialog element itself) closes the lightbox. @param {MouseEvent} event */
  #handleLightboxClick = (event) => {
    if (event.target === this.lightbox) this.lightbox?.close();
  };

  /**
   * The buy box announces the chosen variant on the document. If that variant has its own
   * media, move the gallery to it.
   * @param {Event & { detail?: any }} event
   */
  #handleProductSelect = (event) => {
    const mediaId = event.detail?.mediaId;
    if (!mediaId) return;

    const index = this.slides.findIndex((slide) => slide.getAttribute('data-mbb-media-id') === String(mediaId));
    if (index >= 0) this.select(index);
  };
}

if (!customElements.get('mbb-product-gallery')) {
  customElements.define('mbb-product-gallery', MbbProductGallery);
}
